import type {PlainRevaSelector, RevaRootState} from "../redux/store";

/**
 * Pseudo-selectors that ignore the state and pass through the Nth extra positional argument, for
 * use as input selectors in `createSelector`. The argument type can be specified via a TypeScript
 * instantiation expression — e.g. `selectArg1<MyType>` — which then types the corresponding
 * combiner parameter; used bare, the argument is `unknown`.
 */
export const selectArg1 = <A = unknown>(_state: RevaRootState, arg1: A): A => arg1;
export const selectArg2 = <A = unknown>(_state: RevaRootState, _arg1: unknown, arg2: A): A => arg2;
export const selectArg3 = <A = unknown>(_state: RevaRootState, _arg1: unknown, _arg2: unknown, arg3: A): A => arg3;

export const selectArg = selectArg1;

/**
 * Curry a selector with its additional positional arguments: `currySelector(sel)(...args)(state)`.
 * @see https://reselect.js.org/faq/#how-can-i-make-a-curried-selector
 */
export function currySelector<Args extends unknown[], R>(
    selector: (state: RevaRootState, ...args: Args) => R,
): (...args: Args) => PlainRevaSelector<R> {
    const curriedSelector =
        (...args: Args): PlainRevaSelector<R> =>
        (state) =>
            selector(state, ...args);
    return Object.assign(curriedSelector, selector);
}
